
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User, Session } from '@supabase/supabase-js';
import { AuthState } from '@/types/auth';
import { AUTH_TIMEOUTS } from '@/constants';
import { ensureEmployeeRecord } from '@/utils/auth';

export const useSupabaseAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    session: null,
    loading: true,
  });
  const [refreshCounter, setRefreshCounter] = useState(0);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let isMounted = true;

    const setLoadingTimeout = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (isMounted) {
          console.log('Auth loading timeout reached, forcing loading to false');
          setAuthState(prev => ({ ...prev, loading: false }));
        }
      }, AUTH_TIMEOUTS.SUPABASE_AUTH);
    };

    setLoadingTimeout();

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, session?.user?.email);
        
        if (!isMounted) return;
        
        try {
          // Handle token refresh events specifically
          if (event === 'TOKEN_REFRESHED' && session?.user) {
            console.log('Token refreshed, updating permissions...');
            setRefreshCounter(prev => prev + 1);
          }
          
          if (event === 'SIGNED_IN' && session?.user) {
            console.log('User signed in, processing...');
          }
          
          setAuthState({
            user: session?.user ?? null,
            session,
            loading: false,
          });
          
          if (timeoutId) clearTimeout(timeoutId);
        } catch (error) {
          console.error('Error in auth state change:', error);
          if (isMounted) {
            setAuthState({
              user: session?.user ?? null,
              session,
              loading: false,
            });
          }
          if (timeoutId) clearTimeout(timeoutId);
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      console.log('Initial session check:', session?.user?.email);
      
      if (!isMounted) return;
      
      try {
        if (session?.user) {
          console.log('Existing user found');
        }
        
        setAuthState({
          user: session?.user ?? null,
          session,
          loading: false,
        });
        
        if (timeoutId) clearTimeout(timeoutId);
      } catch (error) {
        console.error('Error in initial session check:', error);
        if (isMounted) {
          setAuthState({
            user: session?.user ?? null,
            session,
            loading: false,
          });
        }
        if (timeoutId) clearTimeout(timeoutId);
      }
    });

    // Set up heartbeat to keep session active
    const heartbeatInterval = setInterval(async () => {
      if (isMounted) {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (session && !error) {
          console.log('Session heartbeat - session is active');
        } else if (error) {
          console.error('Session heartbeat error:', error);
        }
      }
    }, 5 * 60 * 1000); // Every 5 minutes

    return () => {
      isMounted = false;
      subscription.unsubscribe();
      if (timeoutId) clearTimeout(timeoutId);
      clearInterval(heartbeatInterval);
    };
  }, []);

  // This function is now imported from utils/auth.ts

  const signUp = async (email: string, password: string, options?: { data?: { full_name?: string; display_name?: string; } }) => {
    const redirectUrl = `${window.location.origin}/`;
    
    console.log('Starting sign up process for:', email);
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: options?.data
      }
    });
    
    if (!error && data.user) {
      console.log('Sign up successful, user created:', data.user.email);
    }
    
    return { data, error };
  };

  const signIn = async (email: string, password: string) => {
    console.log('Starting sign in process for:', email);
    
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (!error && data.user) {
      console.log('Sign in successful:', data.user.email);
    }
    
    return { data, error };
  };

  const signOut = async () => {
    console.log('Starting sign out process');
    const { error } = await supabase.auth.signOut();
    return { error };
  };

  const refreshSession = async () => {
    console.log('Manually refreshing session...');
    const { data: { session }, error } = await supabase.auth.refreshSession();
    if (error) {
      console.error('Failed to refresh session:', error);
      return { error };
    }
    if (session) {
      console.log('Session refreshed successfully');
      setAuthState(prev => ({ ...prev, session, user: session.user }));
    }
    return { data: session, error: null };
  };

  return {
    ...authState,
    signUp,
    signIn,
    signOut,
    refreshSession,
    refreshCounter,
  };
};
